import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { closeModal } from 'data/reducers/modalReducer';
import {
	fetchAvailableProperties,
	addPropertyToPortfolioWithState,
	clearPortfolioErrors,
	fetchPortfolioById,
} from 'data/reducers/portfoliosManagementReducer';
import { showToast } from 'data/reducers/toastReducer';
import StandardModal from './StandardModal';
import { FormSelect } from '../components/Form';
import GoogleAddressSearchV2 from '../components/addressSearch/GoogleAddressSearchV2';
import _ from 'lodash';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import './AddPortfolioPropertyModal.css';

// Mirrors CheckPlanLimitsResponse::LIMIT_EXCEEDED_ERROR_CODE (violerts
// app/Entities/CheckPlanLimitsResponse.php) -- the machine-readable value the
// backend puts under the `error` key of the 403 body when a plan limit blocks
// the request.
const PLAN_LIMIT_EXCEEDED_ERROR_CODE = 'PLAN_LIMIT_EXCEEDED';

// Where an in-app "Upgrade" action should send the user. This is the same
// destination UpgradeIncentiveModal already routes to for its upgrade CTA.
const UPGRADE_ROUTE = '/pricing';

/**
 * Derive what to show for a rejected addPropertyToPortfolioWithState() call.
 *
 * Returns null when `error` is not a plan-limit rejection, so the caller
 * falls through to the ordinary toast. When it is, returns the backend's own
 * message/suggestion text plus the structured usage fields from
 * `errorDetails` -- never new copy, since the backend owns the wording for
 * the standard, enterprise-custom, and shared-seat variants alike.
 *
 * `errorDetails.upgradeRequired` is what distinguishes the standard
 * (upgrade-eligible) case from the enterprise-custom-limit and shared-seat
 * cases: in both of the latter, `upgradeRequired` is false and an "Upgrade"
 * action would be a dead end for the person seeing it, so no action is
 * offered.
 *
 * @param {*} error The value the addPropertyToPortfolioWithState() promise
 *   rejects with.
 * @returns {{message: string, suggestionMessage: string|null, canUpgrade: boolean, planName: string|null, currentUsage: number|null, planLimit: number|null, isUnlimited: boolean}|null}
 */
export const getPlanLimitPrompt = (error) => {
	if (!error || error.errorCode !== PLAN_LIMIT_EXCEEDED_ERROR_CODE) {
		return null;
	}

	const details = error.errorDetails || {};
	const suggestions = error.suggestions || {};

	return {
		message: error.message || '',
		suggestionMessage: suggestions.message || null,
		canUpgrade: Boolean(details.upgradeRequired),
		planName: details.planName || null,
		currentUsage: Number.isFinite(details.currentUsage)
			? details.currentUsage
			: null,
		planLimit: Number.isFinite(details.planLimit) ? details.planLimit : null,
		isUnlimited: Boolean(details.isUnlimited),
	};
};

const AddPortfolioPropertyModal = ({ options }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { active: activeOrganization, actions: orgActions } =
		useOrganizations();
	const {
		isAddingProperty,
		addPropertyError,
		availableProperties,
		isLoadingAvailableProperties,
		availablePropertiesError,
	} = useSelector((state) => state.portfoliosManagement);

	// current logged-in user id to support "Me" option
	const currentUserId = useSelector((state) => state.user?.userData?.id);

	// State for managing search vs existing property modes
	const [searchMode, setSearchMode] = useState(true);
	const [searchedProperty, setSearchedProperty] = useState(null);
	const [isSearching, setIsSearching] = useState(false);
	const [searchError, setSearchError] = useState(null);
	const [orgStats, setOrgStats] = useState(null);
	// Set only for a plan-limit (403 PLAN_LIMIT_EXCEEDED) rejection; every
	// other error keeps going through the plain toast below.
	const [planLimitPrompt, setPlanLimitPrompt] = useState(null);

	// The prompt describes the outcome of exactly one add attempt, including
	// that attempt's usage/limit numbers, so it is dropped whenever a new
	// attempt begins or the user switches selection mode. Without this it is
	// only ever reset on mount and survives - stale - for as long as the modal
	// stays open.
	const clearPlanLimitPrompt = useCallback(() => {
		setPlanLimitPrompt(null);
	}, []);

	// Listen to property creation to add it to portfolio
	const { propertyData } = useSelector((state) => state.property);

	// Get portfolioSetId and users from options
	const portfolioSetId = options?.portfolioSetId;
	const portfolioUsers = useMemo(
		() => options?.portfolioUsers || [],
		[options?.portfolioUsers],
	);

	// React Hook Form setup
	const { control, handleSubmit, watch, setValue } = useForm({
		defaultValues: {
			userId: '',
			propertyId: '',
		},
		mode: 'onChange',
	});

	// Watch userId to fetch properties when user changes
	const selectedUserId = watch('userId');

	// Check if "Me" is selected to show search option
	const isCurrentUserSelected =
		_.toString(selectedUserId) === _.toString(currentUserId);

	useEffect(() => {
		if (currentUserId) {
			setValue('userId', currentUserId);
		}
	}, [currentUserId, setValue]);

	useEffect(() => {
		if (activeOrganization?.id) {
			orgActions.getStats({ id: activeOrganization.id }).then((response) => {
				if (response?.data) {
					setOrgStats(response.data);
				}
			});
		}
	}, [activeOrganization, orgActions]);
	// Check if at limit
	const isAtLimit = useMemo(() => {
		if (!orgStats) return false;
		return (orgStats?.properties_remaining || 0) <= 0;
	}, [orgStats]);

	// Handle adding the searched property to portfolio
	const handleAddSearchedProperty = useCallback(
		async (property) => {
			setIsSearching(false);
			clearPlanLimitPrompt();
			try {
				// Add the property to the portfolio
				await dispatch(
					addPropertyToPortfolioWithState(portfolioSetId, property.id),
				);

				// Show success message
				dispatch(
					showToast({
						title: 'Success!',
						body: `Property "${property.address}" has been added to the portfolio successfully.`,
						type: 'success',
					}),
				);

				// Close modal on success
				dispatch(closeModal());

				// Fetch portfolio by ID
				dispatch(fetchPortfolioById(portfolioSetId));
			} catch (error) {
				// console.error('Error adding searched property to portfolio:', error);
				const planLimit = getPlanLimitPrompt(error);
				if (planLimit) {
					setPlanLimitPrompt(planLimit);
					return;
				}
				dispatch(
					showToast({
						title: 'Error!',
						body:
							error?.message ||
							'Failed to add property to portfolio. Please try again.',
						type: 'warning',
					}),
				);
			}
		},
		[dispatch, portfolioSetId, clearPlanLimitPrompt],
	);

	// Clear any existing errors when the modal opens
	useEffect(() => {
		dispatch(clearPortfolioErrors());
		setSearchMode(true);
		setSearchedProperty(null);
		clearPlanLimitPrompt();
	}, [dispatch, clearPlanLimitPrompt]);

	const handleUpgradeClick = () => {
		dispatch(closeModal());
		history.push(UPGRADE_ROUTE);
	};

	// Fetch available properties when user is selected (but not in search mode)
	useEffect(() => {
		if (selectedUserId && portfolioSetId && !searchMode) {
			dispatch(fetchAvailableProperties(portfolioSetId, selectedUserId));
		}
	}, [selectedUserId, portfolioSetId, searchMode, dispatch]);

	// Auto-add property when it's created via address search
	useEffect(() => {
		if (propertyData && searchedProperty && portfolioSetId) {
			// Property was created, now add it to the portfolio
			// handleAddSearchedProperty(propertyData);
		}
	}, [
		propertyData,
		searchedProperty,
		portfolioSetId,
		handleAddSearchedProperty,
	]);

	// Format users from props for dropdown and prepend a "Me" option if available
	const userOptions = useMemo(() => {
		const base = portfolioUsers.map((user) => ({
			value: user.id,
			label: `${user.name} (${user.email})`,
		}));
		if (currentUserId) {
			return [
				{ value: currentUserId, label: 'Me' },
				...base.filter((u) => u.value !== currentUserId),
			];
		}
		return base;
	}, [portfolioUsers, currentUserId]);

	// Format available properties for dropdown
	const propertyOptions = useMemo(() => {
		if (searchMode && searchedProperty) {
			// Show the searched property as an option
			return [
				{
					value: searchedProperty.id || 'searched',
					label: searchedProperty.address || 'Searched Property',
				},
			];
		}
		return availableProperties.map((property) => ({
			value: property.id,
			label: property.address || `${property.block}/${property.lot}`,
		}));
	}, [availableProperties, searchMode, searchedProperty]);

	// Handle address selection from Google Places
	const handleAddressSelect = async (property) => {
		setIsSearching(false);
		if (!property?.address) {
			setSearchedProperty(null);
			setSearchError('Selected place does not have a valid address.');

			return;
		}

		await handleAddSearchedProperty(property);

		setSearchError(null);
		setSearchedProperty({ address: property.address });
		// Clear any existing errors
		dispatch(clearPortfolioErrors());
	};

	// Form submission handler
	const onSubmit = async (data) => {
		clearPlanLimitPrompt();
		try {
			// Get user and property details for success message

			const selectedProperty = availableProperties.find(
				(prop) => prop.id === data.propertyId,
			);

			// Use Redux action to add property to portfolio
			await dispatch(
				addPropertyToPortfolioWithState(portfolioSetId, data.propertyId),
			);

			// Show success message
			dispatch(
				showToast({
					title: 'Success!',
					body: `Property "${
						selectedProperty?.address || 'Selected property'
					}" has been added to the portfolio successfully.`,
					type: 'success',
				}),
			);

			// Close modal on success
			dispatch(closeModal());

			dispatch(fetchPortfolioById(portfolioSetId));
		} catch (error) {
			// console.error('Error adding property to portfolio:', error);

			// A structured plan-limit rejection gets its own prompt instead of
			// the bare toast below -- see getPlanLimitPrompt().
			const planLimit = getPlanLimitPrompt(error);
			if (planLimit) {
				setPlanLimitPrompt(planLimit);
				return;
			}

			// Error will be handled by Redux and shown in addPropertyError
			// Show fallback toast error
			dispatch(
				showToast({
					type: 'warning',
					title: 'Error!',
					body:
						error.response?.data?.message ||
						error.message ||
						'Failed to add property to portfolio. Please try again.',
				}),
			);
		}
	};

	const handleCancel = () => {
		dispatch(closeModal());
	};

	return (
		<StandardModal
			title="Add Property to Portfolio"
			className="add-property-modal violerts-v3-root"
			size="md"
			onClose={handleCancel}
			closeOnOutsideClick
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="modal-body">
					{isAtLimit && (
						<div className="alert alert-danger">
							<i className="bi bi-exclamation-triangle me-2"></i>
							<strong>Property Limit Reached!</strong>
							<p className="mb-0 mt-2">
								Your organization has reached its property limit (
								{orgStats.properties_limit}). Please upgrade your plan to add
								more properties.
							</p>
						</div>
					)}
					{planLimitPrompt && (
						<div className="mb-4 alert alert-danger mt-2 mb-0 plan-limit-prompt">
							<i className="bi bi-exclamation-triangle me-2"></i>
							<div>{planLimitPrompt.message}</div>
							{(planLimitPrompt.planName ||
								planLimitPrompt.currentUsage !== null) && (
								<div className="mt-2">
									{planLimitPrompt.planName && (
										<div>
											Plan: <strong>{planLimitPrompt.planName}</strong>
										</div>
									)}
									{planLimitPrompt.currentUsage !== null && (
										<div>
											Usage:{' '}
											<strong>
												{planLimitPrompt.currentUsage} /{' '}
												{planLimitPrompt.isUnlimited
													? 'Unlimited'
													: planLimitPrompt.planLimit}
											</strong>
										</div>
									)}
								</div>
							)}
							{planLimitPrompt.suggestionMessage && (
								<p className="mb-0 mt-2">
									{planLimitPrompt.suggestionMessage}
								</p>
							)}
							{planLimitPrompt.canUpgrade && (
								<button
									type="button"
									className="btn btn-sm btn-primary mt-2"
									onClick={handleUpgradeClick}
								>
									Upgrade Plan
								</button>
							)}
						</div>
					)}

					{/* The thunk files the same rejection into Redux as
					addPropertyError, so this legacy alert would repeat the
					sentence the structured prompt above already shows. */}
					{!planLimitPrompt && (addPropertyError || availablePropertiesError) && (
						<div className="mb-4 alert alert-danger mt-2 mb-0">
							<i className="bi bi-exclamation-triangle me-2"></i>
							{typeof (addPropertyError || availablePropertiesError) ===
							'string'
								? addPropertyError || availablePropertiesError
								: (addPropertyError || availablePropertiesError).message ||
									(addPropertyError || availablePropertiesError).data
										?.message ||
									'Error adding property to portfolio. Please try again.'}
						</div>
					)}

					{searchError && (
						<div className="mb-4 alert alert-danger mt-2 mb-0">
							<i className="bi bi-exclamation-triangle me-2"></i>
							{searchError}
						</div>
					)}

					{/* User Selection */}
					{/**
					 *  Put this selection hidden decided always should be me or search property
					 */}
					{false && (
						<div className="mb-3">
							<FormSelect
								name="userId"
								control={control}
								label="Select User"
								options={userOptions}
								placeholder={
									userOptions.length === 0
										? 'No users available'
										: 'Choose a user...'
								}
								required
							/>
							<div className="form-text">
								<i className="bi bi-info-circle me-1"></i>
								Select the user who will have access to the property.
							</div>
						</div>
					)}

					{/* Property Selection Mode Tabs (shown when current user is selected - matching Settings page theme) */}
					{isCurrentUserSelected && (
						<div className="add-property-tabs tw-flex tw-items-center tw-mb-4">
							<button
								type="button"
								className={`add-property-tab-btn tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-l-[4px] tw-transition-colors ${
									!searchMode
										? 'active tw-bg-[#161616] tw-text-white'
										: 'tw-bg-white tw-text-text-primary tw-border tw-border-[#C4C4C4] hover:tw-bg-[#F7F7F7]'
								}`}
								onClick={() => {
									if (searchMode) {
										setSearchMode(false);
										setSearchedProperty(null);
										setSearchError(null);
										clearPlanLimitPrompt();
										setValue('propertyId', '');
									}
								}}
								disabled={isAddingProperty || isSearching}
							>
								Existing Properties
							</button>
							<button
								type="button"
								className={`add-property-tab-btn tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-r-[4px] -tw-ml-[1px] tw-transition-colors ${
									searchMode
										? 'active tw-bg-[#161616] tw-text-white'
										: 'tw-bg-white tw-text-text-primary tw-border tw-border-[#C4C4C4] hover:tw-bg-[#F7F7F7]'
								}`}
								onClick={() => {
									if (!searchMode) {
										setSearchMode(true);
										setSearchedProperty(null);
										setSearchError(null);
										clearPlanLimitPrompt();
										setValue('propertyId', '');
									}
								}}
								disabled={isAddingProperty || isSearching}
							>
								Search New Property
							</button>
						</div>
					)}

					{/* Google Address Search - shown when in search mode */}
					{isCurrentUserSelected && searchMode ? (
						<div className="mb-3">
							<GoogleAddressSearchV2
								label="Search for Property"
								placeholder="Enter address to search..."
								onSelect={handleAddressSelect}
								isSearching={(value) => {
									setIsSearching(value || true);
									setSearchError(null);
								}}
							/>
							<div className="form-text">
								<i className="bi bi-info-circle me-1"></i>
								Search for a new property to add to your portfolio.
							</div>
							{isSearching && (
								<div className="text-info mt-2">
									<i className="bi bi-hourglass-split me-1"></i>
									Searching for property...
								</div>
							)}
							{searchedProperty && !isSearching && false && (
								<div className="alert alert-info mt-2">
									<i className="bi bi-check-circle me-2"></i>
									Property found: <strong>{searchedProperty.address}</strong>
								</div>
							)}
						</div>
					) : (
						/* Standard Property Selection - shown when not in search mode */
						<div className="mb-3">
							<FormSelect
								name="propertyId"
								control={control}
								label="Select Property"
								options={propertyOptions}
								placeholder={
									!selectedUserId
										? 'Please select a user first'
										: isLoadingAvailableProperties
											? 'Loading properties...'
											: propertyOptions.length === 0
												? 'No available properties'
												: 'Choose a property...'
								}
								required={!searchMode}
								disabled={!selectedUserId || isLoadingAvailableProperties}
								loading={isLoadingAvailableProperties}
							/>
							<div className="form-text">
								<i className="bi bi-info-circle me-1"></i>
								{isCurrentUserSelected && !searchMode
									? 'Select from your existing properties or search for a new one.'
									: 'Only properties not already in the portfolio set are shown.'}
							</div>
							{availablePropertiesError && (
								<div className="text-danger mt-1">
									<i className="bi bi-exclamation-circle me-1"></i>
									{availablePropertiesError.message || availablePropertiesError}
								</div>
							)}
						</div>
					)}
				</div>

				<div className="modal-footer">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={handleCancel}
						disabled={isAddingProperty}
					>
						Cancel
					</button>
					<button
						type={searchMode && searchedProperty ? 'button' : 'submit'}
						className="btn btn-primary"
						disabled={
							isAddingProperty ||
							isSearching ||
							!selectedUserId ||
							(searchMode ? !searchedProperty : propertyOptions.length === 0)
						}
						onClick={
							searchMode && searchedProperty
								? () =>
										handleAddSearchedProperty(propertyData || searchedProperty)
								: undefined
						}
					>
						{isAddingProperty ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									role="status"
								/>
								Adding...
							</>
						) : (
							<>
								<i className="bi bi-plus-lg me-2" />
								Add Property
							</>
						)}
					</button>
				</div>
			</form>
		</StandardModal>
	);
};

export default AddPortfolioPropertyModal;
